import { Alert } from '@ksyos/design-system';
import { some } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import type { IAsset } from '@frontend/components/interfaces/asset';
import type { IMedication } from '@frontend/components/interfaces/medication';
import type { ICustomControlRequestAddEditProps } from '@frontend/components/pages/customControls/interfaces';
import type { ValidationErrors } from '@frontend/utils/validation';
import { getServiceLabel } from '@common/customControls/getServiceLabel';
import { ConclusieDiagnoseOption } from '@common/enums/conclusieDiagnoseOption';
import { DermaConsultationAssetDropzoneExtraActions } from '@common/enums/dermaConsultationAssetDropzoneExtraActions';
import { FilesUrlBase } from '@common/enums/filesUrlBase';
import { PractitionerRoleInRequest } from '@common/enums/practitionerRoleInRequest';
import { ProcessEntity } from '@common/enums/processEntity';
import { ToastType } from '@common/enums/toastType';
import { getObjectKeys } from '@common/utils/getObjectKeys';
import { getOrderedAssets, getOrderedPhotoAssetIds, getPhotoAssetIdKey } from '@common/utils/photoAssetUtils';
import { attachmentLimit } from '@common/validation/asset';
import { huidkankerOptions } from '@common/validation/customControls/dermatology';
import { nullDiagnosis } from '@common/validation/diagnosis';
import { CancelButton } from '@frontend/components/elements/carePathButtons/CancelButton';
import { SaveButton } from '@frontend/components/elements/carePathButtons/SaveButton';
import { SaveSendButton } from '@frontend/components/elements/carePathButtons/SaveSendButton';
import {
    ButtonContainer,
    Form,
    FormDivider,
    HeaderDivider,
    InputCheckbox,
    InputRadioGroup,
} from '@frontend/components/elements/common';
import { FormContextProvider } from '@frontend/components/elements/formContext';
import { FormLabel } from '@frontend/components/elements/forms/FormLabel';
import { HorizontalFormGroup } from '@frontend/components/elements/forms/HorizontalFormGroup';
import { InputContainer } from '@frontend/components/elements/forms/InputContainer';
import { InputTextCheckbox } from '@frontend/components/elements/InputTextCheckbox';
import { ProceedWithoutFiles } from '@frontend/components/elements/ProceedWithoutFiles';
import { DropdownField } from '@frontend/components/fields/Dropdown';
import { RadioGroupField } from '@frontend/components/fields/RadioGroup';
import { TextAreaField } from '@frontend/components/fields/TextArea';
import { TriStateField } from '@frontend/components/fields/TriState';
import { ModalUploadPictureFromDermaApp } from '@frontend/components/pages/customControls/DermaC/v1/ModalUploadPictureFromDermaApp';
import {
    addFormAnalytics,
    addFormConversion,
    addTrackFormSubmit,
} from '@frontend/components/pages/customControls/utils/formAnalytics';
import { AssetDropzoneWithActions } from '@frontend/components/pages/customControls/utils/v1/AssetDropzoneWithActions';
import { CareProviderSelector } from '@frontend/components/pages/customControls/utils/v1/CareProviderSelector';
import { Icd10Selections } from '@frontend/components/pages/customControls/utils/v1/Icd10/Icd10Selections';
import { MedicationsAddEdit } from '@frontend/components/pages/customControls/utils/v1/medications/MedicationsAddEdit';
import { NewOrEditRequestProcessContainer } from '@frontend/components/pages/customControls/utils/v1/NewOrEditRequestProcessContainer';
import { ServiceConsumerSupervisorSelector } from '@frontend/components/pages/customControls/utils/v1/ServiceConsumerSupervisorSelector';
import { ServiceOrganizationProvider } from '@frontend/components/pages/customControls/utils/v1/ServiceOrganizationProvider';
import { ProfessionalSummaryData } from '@frontend/components/pages/professionalSummaryData/ProfessionalSummaryData';
import { getJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import { checkPreviousExaminationHasAssets } from '@frontend/utils/checkPreviousExaminationHasAssets';
import { showServiceToast } from '@frontend/utils/showToast';
import { DermaSnelCscV1FollowingSteps } from './DermaSnelCscV1FollowingSteps';

export interface IDermaSnelCscV1RequestAddEditCustomControlData {
    conclusie_diagnose: ConclusieDiagnoseOption;
    huidkanker_familie: boolean | null;
    huidkanker_familie_option: string | null;
    huidkanker_familie_option_anders: string | null;
    huidkanker_voorgeschiedenis: boolean | null;
    huidkanker_voorgeschiedenis_option: string | null;
    huidkanker_voorgeschiedenis_option_anders: string | null;
    klachten: IKlachten;
    klachten_verandering_sinds: string | null;
    lichaamsdeel: string | null;
    moedervlek: string | null;

    toelichting: string | null;
    veranderingen_moedervlek: IVeranderingenMoedervlek;
    vraag: IVraag;
    werkdiagnose: IWerkdiagnose;
}

interface IKlachten {
    anders: string | null;
    bloeden: boolean;
    cosmetisch: boolean;
    jeuk: boolean;
    pijn: boolean;
}

interface IVeranderingenMoedervlek {
    grootte: boolean;
    kleur: boolean;
    vorm: boolean;
}

interface IWerkdiagnose {
    atypische_naevus: boolean;
    naevus_naevocellularis: boolean;
    verdenking_melanoom: boolean;
}

interface IVraag {
    anders: string | null;
    diagnostiek_en_behandeling: boolean;
    expectatief_beleid: boolean;
    verdacht_van_kwaadaardigheid: boolean;
}

const huidkankerSelectableOptions = huidkankerOptions.map((value) => {
    return { value, text: value };
});

export function DermaSnelCscV1RequestAddEdit(props: ICustomControlRequestAddEditProps) {
    const [buttonDisabled, setButtonDisabled] = useState<boolean>();
    const [andersCheck, setAndersCheck] = useState(false);
    const [showProceedWithoutFiles, setShowProceedWithoutFiles] = useState(false);
    const [isVraagAndersChecked, setIsVraagAndersChecked] = useState(!!props.request.customControlData.vraag.anders);
    const [showUploadFromAppModal, setShowUploadFromAppModal] = useState(false);
    const [orderedAssets, setOrderedAssets] = useState<IAsset[]>([]);

    const intervalRef = useRef<NodeJS.Timeout | undefined>(undefined);
    const formRef = useRef<HTMLFormElement>(null);
    const saveSendButtonRef = useRef<HTMLButtonElement>(null);

    const assetsLimitExceeded =
        (props.request.customControlData.files?.length || 0 + props.request.assets.length) > attachmentLimit;

    useEffect(() => {
        if (assetsLimitExceeded === false && props.request.id && showUploadFromAppModal === false) {
            reloadAssets();
            intervalRef.current = setInterval(() => reloadAssets(), 4000);
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [assetsLimitExceeded, props.request.id, showUploadFromAppModal]);

    useEffect(() => {
        props.handleChangeCustomControl({
            ...dermaSnelCscV1RequestInitializeCustomControlData(),
            ...props.request.customControlData,
        });
        addFormAnalytics(formRef.current, saveSendButtonRef.current);
    }, []);

    useEffect(() => {
        setOrderedAssets(getOrderedAssets(props.request));
    }, [props.request]);

    return (
        <>
            <NewOrEditRequestProcessContainer
                isFirstItem={props.isFirstItem}
                itemState={props.itemState}
                process={props.process}
                service={props.service}
                title={getServiceLabel(props.service.code, 'request', props.service.name)}
            >
                <Form
                    formRef={formRef}
                    id="DermaSnelCscV1RequestAddEditId"
                    name="DermaSnelCscV1RequestAddEdit"
                    onSubmit={async () => null}
                >
                    {addEditForm()}

                    <ButtonContainer
                        alignRight
                        buttons={[
                            <CancelButton
                                cancel={props.onCancel}
                                entity={ProcessEntity.request}
                                isExisting={!!props.request.id}
                                key={'cancelButton'}
                                serviceCode={props.service.code}
                                serviceType={props.service.serviceType}
                            />,
                            <SaveButton
                                buttonDisabled={buttonDisabled || assetsLimitExceeded}
                                key={'saveButton'}
                                save={async () => {
                                    await saveRequest('draft', false);
                                }}
                            />,
                            <SaveSendButton
                                buttonDisabled={buttonDisabled || assetsLimitExceeded}
                                key={'saveSendButton'}
                                saveSend={async () => {
                                    await saveRequest('final', false);
                                }}
                                saveSendButtonRef={saveSendButtonRef}
                            />,
                        ]}
                    />
                </Form>
            </NewOrEditRequestProcessContainer>

            {showProceedWithoutFiles && (
                <ProceedWithoutFiles
                    entity={ProcessEntity.request}
                    onNo={async () => {
                        setShowProceedWithoutFiles(false);
                        setButtonDisabled(false);
                    }}
                    onYes={async () => {
                        setShowProceedWithoutFiles(false);
                        await saveRequest('final', true);
                    }}
                    serviceType={props.service.serviceType}
                />
            )}
        </>
    );

    function addEditForm() {
        const customControlData: IDermaSnelCscV1RequestAddEditCustomControlData = props.request.customControlData;

        return (
            <FormContextProvider
                value={{
                    errors: props.errors,
                    item: customControlData,
                    onChange: async (key, value) => await props.handleChangeCustomControl({ [key]: value }),
                    onDoValidate: async (key) => await props.validateOneCustomControl(key),
                    placeholderGroupName: 'request',
                }}
            >
                <CareProviderSelector
                    {...props}
                    entity={props.request}
                />

                <ServiceOrganizationProvider
                    errors={props.errors}
                    initialOrganizationId={props.request.selectedDiagnosticReportProviderOrganizationId}
                    initialPractitionerId={props.request.selectedDiagnosticReportProviderPractitionerId}
                    onChange={async (organizationId, practitionerId) => {
                        await props.handleChange('selectedDiagnosticReportProviderOrganizationId', organizationId);
                        await props.handleChange('selectedDiagnosticReportProviderPractitionerId', practitionerId);
                    }}
                    onDoValidate={async () => await props.validateOne('selectedDiagnosticReportProviderOrganizationId')}
                    roleOfPractitionersInList={PractitionerRoleInRequest.specialist}
                    searchInNetwork={true}
                    serviceProviderServiceId={props.service.id}
                    serviceType={props.service.serviceType}
                />

                <ServiceConsumerSupervisorSelector {...props} />

                <FormDivider />

                <HeaderDivider text={translateKey('diagnosticReport.examination')} />

                {props.request.id && (
                    <ModalUploadPictureFromDermaApp
                        isOpen={showUploadFromAppModal}
                        onCancel={async () => {
                            setShowUploadFromAppModal(false);
                        }}
                        onProceed={async () => {
                            setShowUploadFromAppModal(false);
                            await props.reloadAssets();
                        }}
                        requestId={props.request.id}
                    />
                )}

                {assetsLimitExceeded && (
                    <Alert appearance="warning">
                        Je hebt te veel bestanden toegevoegd. Blijf binnen de limiet van 12 bestanden.
                    </Alert>
                )}

                {!showUploadFromAppModal && (
                    <AssetDropzoneWithActions
                        assets={orderedAssets}
                        assetsBaseUrl={FilesUrlBase.requests}
                        cleanDropzoneAfterCompletingFiles={Boolean(props.request.id)}
                        extraActions={[
                            {
                                actionId: DermaConsultationAssetDropzoneExtraActions.uploadFromApp,
                                icon: 'telephone',
                                label: "Foto's via de app toevoegen",
                            },
                        ]}
                        onAction={async (actionId: string) => {
                            if (actionId === DermaConsultationAssetDropzoneExtraActions.uploadFromApp) {
                                await props.onSaveRequest('draft');
                                setShowUploadFromAppModal(true);
                            }
                        }}
                        onChange={handleAssetsChange}
                        onUploading={async (isUploading) => setButtonDisabled(isUploading)}
                        processActivityId={props.request.id}
                        service={props.service}
                        showUseExistingExamination={false}
                        updateNewRequestPreviousRequest={props.updateNewRequestPreviousRequest}
                    />
                )}

                <HorizontalFormGroup>
                    <Alert appearance="info">Let op: Vergeet niet een afbeelding te uploaden!</Alert>
                </HorizontalFormGroup>

                <FormDivider />
                <HeaderDivider text="Anamnese" />
                <RadioGroupField
                    error={props.errors.get('customControlData.moedervlek')}
                    fieldKey="moedervlek"
                    hasErrors={props.errors.has('customControlData.moedervlek')}
                    label="Moedervlek"
                    name="moedervlek"
                    options={['Nieuw', 'Bestaand']}
                />

                <HorizontalFormGroup hasErrors={props.errors.has('customControlData.klachten.anders')}>
                    <FormLabel>Klachten</FormLabel>
                    <InputContainer>
                        <InputCheckbox
                            checked={customControlData.klachten.jeuk}
                            onChange={async (value) => await handleChangeKlachten('jeuk', value)}
                            text="Jeuk"
                        />
                        <InputCheckbox
                            checked={customControlData.klachten.pijn}
                            onChange={async (value) => await handleChangeKlachten('pijn', value)}
                            text="Pijn"
                        />
                        <InputCheckbox
                            checked={customControlData.klachten.bloeden}
                            onChange={async (value) => await handleChangeKlachten('bloeden', value)}
                            text="Bloeden"
                        />
                        <InputCheckbox
                            checked={customControlData.klachten.cosmetisch}
                            onChange={async (value) => await handleChangeKlachten('cosmetisch', value)}
                            text="Cosmetisch"
                        />
                        <InputTextCheckbox
                            error={props.errors.get('customControlData.klachten.anders')}
                            onChange={async (value) => {
                                await handleChangeKlachten('anders', value);
                                await props.validateOneCustomControl('klachten.anders');
                            }}
                            onDoValidate={async () => {
                                await props.validateOneCustomControl('klachten.anders');
                            }}
                            placeholder="Anders, namelijk..."
                            text={customControlData.klachten.anders}
                        />
                    </InputContainer>
                </HorizontalFormGroup>

                <HorizontalFormGroup>
                    <FormLabel>Veranderingen moedervlek</FormLabel>
                    <InputContainer>
                        <InputCheckbox
                            checked={customControlData.veranderingen_moedervlek.grootte}
                            onChange={async (value) => await handleChangeVeranderingenMoedervlek('grootte', value)}
                            text="Grootte"
                        />
                        <InputCheckbox
                            checked={customControlData.veranderingen_moedervlek.vorm}
                            onChange={async (value) => await handleChangeVeranderingenMoedervlek('vorm', value)}
                            text="Vorm"
                        />
                        <InputCheckbox
                            checked={customControlData.veranderingen_moedervlek.kleur}
                            onChange={async (value) => await handleChangeVeranderingenMoedervlek('kleur', value)}
                            text="Kleur(en)"
                        />
                    </InputContainer>
                </HorizontalFormGroup>

                <TextAreaField
                    error={props.errors.get('customControlData.klachten_verandering_sinds')}
                    fieldKey="klachten_verandering_sinds"
                    hasErrors={props.errors.has('customControlData.klachten_verandering_sinds')}
                    label="Klachten/verandering sinds"
                    placeholder="Vermeld wanneer de klachten/veranderingen zijn ontstaan..."
                />
                <TextAreaField
                    error={props.errors.get('customControlData.lichaamsdeel')}
                    fieldKey="lichaamsdeel"
                    hasErrors={props.errors.has('customControlData.lichaamsdeel')}
                    label="Lichaamsdeel"
                    placeholder="Beschrijf op welk lichaamsdeel de moedervlek zich bevindt..."
                />

                <TriStateField
                    error={props.errors.get('customControlData.huidkanker_familie')}
                    fieldKey="huidkanker_familie"
                    hasErrors={props.errors.has('customControlData.huidkanker_familie')}
                    label="Huidkanker in de familie"
                    onChange={async (value) => {
                        const huidkankerUpdate: Partial<IDermaSnelCscV1RequestAddEditCustomControlData> = {
                            huidkanker_familie: value,
                        };
                        if (!value) {
                            huidkankerUpdate.huidkanker_familie_option = null;
                            huidkankerUpdate.huidkanker_familie_option_anders = null;
                        }
                        await props.handleChangeCustomControl(huidkankerUpdate);
                    }}
                />

                {customControlData.huidkanker_familie && (
                    <DropdownField
                        error={props.errors.get('customControlData.huidkanker_familie_option')}
                        fieldKey="huidkanker_familie_option"
                        hasErrors={props.errors.has('customControlData.huidkanker_familie_option')}
                        label=" "
                        onChange={async (value) => {
                            const huidkankerOptionUpdate: Partial<IDermaSnelCscV1RequestAddEditCustomControlData> = {
                                huidkanker_familie_option: value,
                            };
                            if (value !== 'Anders') {
                                huidkankerOptionUpdate.huidkanker_familie_option_anders = null;
                            }
                            await props.handleChangeCustomControl(huidkankerOptionUpdate);
                        }}
                        placeholder="Kies de vorm van huidkanker..."
                        selectOptions={huidkankerSelectableOptions}
                    />
                )}

                {customControlData.huidkanker_familie_option === 'Anders' && (
                    <TextAreaField
                        error={props.errors.get('customControlData.huidkanker_familie_option_anders')}
                        fieldKey="huidkanker_familie_option_anders"
                        hasErrors={props.errors.has('customControlData.huidkanker_familie_option_anders')}
                        label=" "
                        placeholder="Toelichting..."
                    />
                )}

                <TriStateField
                    error={props.errors.get('customControlData.huidkanker_voorgeschiedenis')}
                    fieldKey="huidkanker_voorgeschiedenis"
                    hasErrors={props.errors.has('customControlData.huidkanker_voorgeschiedenis')}
                    label="Huidkanker in eigen voorgeschiedenis"
                    onChange={async (value) => {
                        const huidkankerUpdate: Partial<IDermaSnelCscV1RequestAddEditCustomControlData> = {
                            huidkanker_voorgeschiedenis: value,
                        };
                        if (!value) {
                            huidkankerUpdate.huidkanker_voorgeschiedenis_option = null;
                            huidkankerUpdate.huidkanker_voorgeschiedenis_option_anders = null;
                        }
                        await props.handleChangeCustomControl(huidkankerUpdate);
                    }}
                    value={customControlData.huidkanker_voorgeschiedenis}
                />

                {customControlData.huidkanker_voorgeschiedenis && (
                    <DropdownField
                        error={props.errors.get('customControlData.huidkanker_voorgeschiedenis_option')}
                        fieldKey="huidkanker_voorgeschiedenis_option"
                        hasErrors={props.errors.has('customControlData.huidkanker_voorgeschiedenis_option')}
                        label=" "
                        onChange={async (value) => {
                            const huidkankerOptionUpdate: Partial<IDermaSnelCscV1RequestAddEditCustomControlData> = {
                                huidkanker_voorgeschiedenis_option: value,
                            };
                            if (value !== 'Anders') {
                                huidkankerOptionUpdate.huidkanker_voorgeschiedenis_option_anders = null;
                            }
                            await props.handleChangeCustomControl(huidkankerOptionUpdate);
                        }}
                        placeholder="Kies de vorm van huidkanker..."
                        selectOptions={huidkankerSelectableOptions}
                    />
                )}

                {customControlData.huidkanker_voorgeschiedenis_option === 'Anders' && (
                    <TextAreaField
                        error={props.errors.get('customControlData.huidkanker_voorgeschiedenis_option_anders')}
                        fieldKey="huidkanker_voorgeschiedenis_option_anders"
                        hasErrors={props.errors.has('customControlData.huidkanker_voorgeschiedenis_option_anders')}
                        label=" "
                        placeholder="Toelichting..."
                    />
                )}

                <FormDivider />

                <MedicationsAddEdit
                    entity={ProcessEntity.request}
                    entityId={props.request.id}
                    errors={props.errors}
                    handleChangePatientUsesMedication={async (patientUsesMedication: boolean) =>
                        await props.handleChange('patientUsesMedication', patientUsesMedication)
                    }
                    onChange={async (medications: IMedication[]) =>
                        await props.handleChange('requestMedications', medications)
                    }
                    patientUsesMedication={props.request.patientUsesMedication}
                    validateMedications={async () => await props.validateOne('requestMedications')}
                    validatePatientUsesMedication={async () => await props.validateOne('patientUsesMedication')}
                />

                <ProfessionalSummaryData
                    entity={ProcessEntity.request}
                    entityId={props.request.id}
                    errors={props.errors}
                    handleChangeAllergies={async (allergies) => await props.handleChange('allergies', allergies)}
                    handleChangeEpisodes={async (episodesAndJournal) =>
                        await props.handleChange('requestEpisodesAndJournal', episodesAndJournal)
                    }
                    handleChangeLabResults={async (labResults) =>
                        await props.handleChange('requestLabResults', labResults)
                    }
                    validateEpisodesAndJournals={async () => await props.validateOne('requestEpisodesAndJournal')}
                />

                <HeaderDivider text="Vraag" />
                <HorizontalFormGroup>
                    <FormLabel>Heb je een werkdiagnose?</FormLabel>
                    <InputContainer>
                        <InputRadioGroup
                            name="conclusie_diagnose"
                            onChange={async (value) => {
                                await props.handleChangeCustomControl({ conclusie_diagnose: value });
                                if (value !== ConclusieDiagnoseOption.ja) {
                                    await clearDiagnosis();
                                    await props.handleChangeCustomControl({
                                        werkdiagnose: {
                                            atypische_naevus: false,
                                            naevus_naevocellularis: false,
                                            verdenking_melanoom: false,
                                        },
                                    });
                                    setAndersCheck(false);
                                }
                            }}
                            options={[
                                ConclusieDiagnoseOption.ja,
                                ConclusieDiagnoseOption.nog_geen_diagnose,
                                ConclusieDiagnoseOption.geen_afwijkingen,
                            ]}
                            value={customControlData.conclusie_diagnose}
                        />
                    </InputContainer>
                </HorizontalFormGroup>

                {customControlData.conclusie_diagnose === ConclusieDiagnoseOption.ja && (
                    <>
                        <HorizontalFormGroup
                            hasErrors={
                                props.errors.has('customControlData.werkdiagnose') ||
                                props.errors.has('diagnosis.diagnosisConsiderationIcd10')
                            }
                        >
                            <FormLabel isRequired>Werkdiagnose</FormLabel>
                            <InputContainer>
                                <InputCheckbox
                                    checked={customControlData.werkdiagnose.atypische_naevus}
                                    onChange={async (value) =>
                                        await handleChangeWerkdiagnose('atypische_naevus', value)
                                    }
                                    text="Atypische naevus"
                                />
                                <InputCheckbox
                                    checked={customControlData.werkdiagnose.naevus_naevocellularis}
                                    onChange={async (value) =>
                                        await handleChangeWerkdiagnose('naevus_naevocellularis', value)
                                    }
                                    text="Naevus naevocellularis"
                                />
                                <InputCheckbox
                                    checked={customControlData.werkdiagnose.verdenking_melanoom}
                                    onChange={async (value) =>
                                        await handleChangeWerkdiagnose('verdenking_melanoom', value)
                                    }
                                    text="Verdenking melanoom"
                                />

                                <HorizontalFormGroup noMargin>
                                    <InputCheckbox
                                        checked={andersCheck}
                                        checkOnTextClickedDisabled
                                        onChange={async (value) => {
                                            if (!value) {
                                                await clearDiagnosis();
                                            }
                                            setAndersCheck(value);
                                        }}
                                        onDoValidate={async () => {
                                            await props.validateOne('diagnosis');
                                            await props.validateOneCustomControl('werkdiagnose');
                                        }}
                                    />
                                    <InputContainer className="pr-0">
                                        <Icd10Selections
                                            bold
                                            diagnosis={props.request.diagnosis}
                                            errors={props.errors}
                                            hideWorkingDiagnosisLabel
                                            isRequired
                                            isWorkingDiagnosis={false}
                                            onChange={async (diagnosis) => {
                                                await props.handleChange('diagnosis', diagnosis);
                                                setAndersCheck(
                                                    diagnosis.diagnosisConsiderationIcd10 !== null ||
                                                        diagnosis.considerationThesaurusTermId !== null
                                                );
                                            }}
                                            onDoValidate={async () => {
                                                await props.validateOne('diagnosis');
                                                await props.validateOneCustomControl('werkdiagnose');
                                            }}
                                            placeholder="Beschrijf of klik en kies uit de lijst"
                                            showOnlyWorkingDiagnosis
                                            specialismCode={props.service.specialismCode || ''}
                                        />
                                    </InputContainer>
                                </HorizontalFormGroup>
                            </InputContainer>
                        </HorizontalFormGroup>
                    </>
                )}

                {shouldShowIcd10Selections() && (
                    <Icd10Selections
                        bold
                        diagnosis={props.request.diagnosis}
                        errors={props.errors}
                        hideWorkingDiagnosis
                        isRequired
                        isWorkingDiagnosis={false}
                        onChange={async (diagnosis) => await props.handleChange('diagnosis', diagnosis)}
                        onDoValidate={async () => {
                            await props.validateOne('diagnosis');
                            await props.validateOneCustomControl('werkdiagnose');
                        }}
                        placeholder="Beschrijf of klik en kies uit de lijst"
                        specialismCode={props.service.specialismCode || ''}
                    />
                )}

                <HorizontalFormGroup hasErrors={!!getVraagError()}>
                    <FormLabel isRequired>Vraag aan de dermatoloog</FormLabel>
                    <InputContainer>
                        <InputCheckbox
                            checked={customControlData.vraag.verdacht_van_kwaadaardigheid}
                            onChange={async (value) => await handleChangeVraag('verdacht_van_kwaadaardigheid', value)}
                            onDoValidate={async () => await props.validateOneCustomControl('vraag')}
                            text="Verdacht van kwaadaardigheid?"
                        />
                        <InputCheckbox
                            checked={customControlData.vraag.expectatief_beleid}
                            onChange={async (value) => await handleChangeVraag('expectatief_beleid', value)}
                            onDoValidate={async () => await props.validateOneCustomControl('vraag')}
                            text="Expectatief beleid?"
                        />
                        <InputCheckbox
                            checked={customControlData.vraag.diagnostiek_en_behandeling}
                            onChange={async (value) => await handleChangeVraag('diagnostiek_en_behandeling', value)}
                            onDoValidate={async () => await props.validateOneCustomControl('vraag')}
                            text="Insturen voor diagnostiek en behandeling?"
                        />
                        <InputTextCheckbox
                            error={getVraagError()}
                            expandableTextArea={true}
                            labelStyle={{ width: '100%' }}
                            onChange={async (value, checked) => {
                                await handleChangeVraag('anders', value);
                                setIsVraagAndersChecked(!!checked);
                                await props.validateOneCustomControl('vraag');
                                props.validateOneCustomControl('vraag.anders');
                            }}
                            onDoValidate={async () => {
                                await props.validateOneCustomControl('vraag');
                                props.validateOneCustomControl('vraag.anders');
                            }}
                            placeholder="Anders, namelijk..."
                            text={customControlData.vraag.anders}
                        />
                    </InputContainer>
                </HorizontalFormGroup>

                <TextAreaField
                    error={props.errors.get('customControlData.toelichting')}
                    fieldKey="toelichting"
                    hasErrors={props.errors.has('customControlData.toelichting')}
                    label="Toelichting"
                    placeholder="Een toelichting, opmerkingen of vragen..."
                />

                <DermaSnelCscV1FollowingSteps />
            </FormContextProvider>
        );
    }

    async function saveRequest(saveType: 'draft' | 'final', proceedWithoutFiles: boolean) {
        const formNode = formRef.current;
        const buttonNode = saveSendButtonRef.current;
        setButtonDisabled(true);

        if (!proceedWithoutFiles) {
            addTrackFormSubmit(buttonNode);
        }

        const validatedRequest = await props.validateRequest(saveType);

        if (!validatedRequest || (saveType === 'final' && isVraagAndersInvalid())) {
            setButtonDisabled(false);

            return;
        }

        if (
            !proceedWithoutFiles &&
            saveType === 'final' &&
            props.request.assets.length === 0 &&
            !checkPreviousExaminationHasAssets(props.previousRequest?.procedure)
        ) {
            setShowProceedWithoutFiles(true);

            return;
        }

        const requestId = await props.onSaveRequest(saveType);

        if (saveType === 'draft') {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                props.service,
                'request',
                translateKey('toast.draftDefaultMessage')
            );
            await props.onSave(requestId);
        } else {
            const result = await getJson(
                `/api/organizations/${props.request.selectedDiagnosticReportProviderOrganizationId}`
            );
            showServiceToast(
                ToastType.success,
                'toast.submitSuccess',
                props.service,
                'request',
                translateKey('toast.consultRequestSubmit', result.json.name)
            );
            await props.onSave(requestId);
            addFormConversion(formNode);
        }
    }

    async function handleAssetsChange(assets: IAsset[]) {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }

        const appPhotos = getOrderedPhotoAssetIds(props.request);
        const removedAppPhotos = appPhotos.filter((photo) => assets.every((asset) => asset.id !== photo.photoAssetId));

        for (const { volgnummer } of removedAppPhotos) {
            const photoAssetIdKey = getPhotoAssetIdKey(volgnummer);
            await props.handleChangeCustomControl({
                [photoAssetIdKey]: undefined,
            });
        }

        await props.handleChange('assets', assets);
        await props.onSaveRequest('draft');
    }

    function isVraagAndersInvalid() {
        return isVraagAndersChecked && !props.request.customControlData.vraag.anders;
    }

    function getVraagError() {
        if (isVraagAndersInvalid()) {
            return 'enter_question';
        }

        return props.errors.get('customControlData.vraag.anders') || props.errors.get('vraag');
    }

    async function handleChangeKlachten<K extends keyof IKlachten>(name: K, value: IKlachten[K]) {
        await props.handleChangeCustomControl({
            klachten: {
                ...props.request.customControlData.klachten,
                [name]: value,
            },
        });
    }

    async function handleChangeVeranderingenMoedervlek<K extends keyof IVeranderingenMoedervlek>(
        name: K,
        value: IVeranderingenMoedervlek[K]
    ) {
        await props.handleChangeCustomControl({
            veranderingen_moedervlek: {
                ...props.request.customControlData.veranderingen_moedervlek,
                [name]: value,
            },
        });
    }

    async function handleChangeWerkdiagnose<K extends keyof IWerkdiagnose>(name: K, value: IWerkdiagnose[K]) {
        await props.handleChangeCustomControl({
            werkdiagnose: {
                ...props.request.customControlData.werkdiagnose,
                [name]: value,
            },
        });
    }

    async function handleChangeVraag<K extends keyof IVraag>(name: K, value: IVraag[K]) {
        await props.handleChangeCustomControl({
            vraag: {
                ...props.request.customControlData.vraag,
                [name]: value,
            },
        });
    }

    async function clearDiagnosis() {
        await props.handleChange('diagnosis', {
            ...nullDiagnosis,
        });
    }

    function shouldShowIcd10Selections() {
        if (
            props.request.diagnosis.diagnosisConsiderationIcd10 !== null ||
            props.request.diagnosis.considerationThesaurusTermId !== null
        ) {
            return true;
        }

        if (some(props.request.customControlData.werkdiagnose)) {
            return true;
        }

        return false;
    }

    async function reloadAssets() {
        await props.reloadAssets();
    }
}

export function validateErrorsForVraag(
    customControlData: IDermaSnelCscV1RequestAddEditCustomControlData,
    errors: ValidationErrors
) {
    const vraagWasFilledIn = Object.values(customControlData.vraag).some((value) => {
        return value !== false && value !== null;
    });

    return vraagWasFilledIn ? errors.remove('vraag') : errors.set('vraag', 'any_selected');
}

export function dermaSnelCscV1RequestValidateOneCustomControlData(
    name: string,
    customControlData: IDermaSnelCscV1RequestAddEditCustomControlData,
    errors: ValidationErrors
) {
    if (name === 'vraag') {
        return validateErrorsForVraag(customControlData, errors);
    } else if (name === 'werkdiagnose') {
        return validateErrorsForWerkdiagnose(customControlData, errors);
    }

    return errors;
}

export function dermaSnelCscV1RequestValidateCustomControlData(
    customControlData: IDermaSnelCscV1RequestAddEditCustomControlData,
    errors: ValidationErrors
) {
    return validateErrorsForVraag(customControlData, errors);
}

function validateErrorsForWerkdiagnose(
    customControlData: IDermaSnelCscV1RequestAddEditCustomControlData,
    errors: ValidationErrors
) {
    let validationErrors = errors;

    if (customControlData.conclusie_diagnose !== ConclusieDiagnoseOption.ja) {
        return validationErrors.remove('customControlData.werkdiagnose');
    }

    const isWerkdiagnoseFilled = getObjectKeys(customControlData.werkdiagnose).some(
        (key) => customControlData.werkdiagnose[key]
    );

    if (!isWerkdiagnoseFilled) {
        validationErrors = validationErrors.set('customControlData.werkdiagnose', 'any_selected');
    } else {
        validationErrors = validationErrors.remove('customControlData.werkdiagnose');
        validationErrors = validationErrors.remove('diagnosis.diagnosisConsiderationIcd10');
    }

    return validationErrors;
}

export function dermaSnelCscV1RequestInitializeCustomControlData() {
    return {
        conclusie_diagnose: null,
        huidkanker_familie: null,
        huidkanker_familie_option: null,
        huidkanker_familie_option_anders: null,
        huidkanker_voorgeschiedenis: null,
        huidkanker_voorgeschiedenis_option: null,
        huidkanker_voorgeschiedenis_option_anders: null,
        klachten: {
            anders: null,
            bloeden: false,
            cosmetisch: false,
            jeuk: false,
            pijn: false,
        },
        klachten_verandering_sinds: null,
        lichaamsdeel: null,
        moedervlek: null,
        toelichting: null,
        veranderingen_moedervlek: {
            grootte: false,
            kleur: false,
            vorm: false,
        },
        vraag: {
            anders: null,
            diagnostiek_en_behandeling: false,
            expectatief_beleid: false,
            verdacht_van_kwaadaardigheid: false,
        },
        werkdiagnose: {
            atypische_naevus: false,
            naevus_naevocellularis: false,
            verdenking_melanoom: false,
        },
    };
}
